import React, { createContext, useContext, useState, useMemo, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import Constants from './Constants';

const AuthContext = createContext(null);

export function has_permission(user_permissions, permission_name) {
	return user_permissions.indexOf(permission_name) !== -1;
}

export function AuthProvider(props) {
	const initialToken = localStorage.getItem(Constants.TOKEN_NAME) || null;

	const [token, setToken] = useState(initialToken);
	const [subscription_features, set_subscription_features] = useState([]);
	const [user_permissions, set_user_permissions] = useState([]);
	const [is_superadmin, set_is_superadmin] = useState(false);
	const [auth_loading, set_auth_loading] = useState(!!initialToken);
	const interceptor_ref = useRef(null);

	const logout = useCallback(async function(session_expired) {
		const stored_token = localStorage.getItem(Constants.TOKEN_NAME);
		if(stored_token) {
			try {
				const headers = { Authorization: 'Bearer ' + stored_token };
				await axios.post(Constants.SERVER_URL + '/api/auth/logout', {}, { headers });
			} catch (error) {
				console.log('logout - server-side revoke failed: ', error);
			}
		}

		localStorage.removeItem(Constants.TOKEN_NAME);
		localStorage.removeItem(Constants.USER_ID);
		setToken(null);
		if(session_expired === true) {
			window.location.replace('/login?session_expired=1');
		}
		else {
			window.location.replace('/login');
		}
	}, []);

	useEffect(function() {
		if(interceptor_ref.current !== null) {
			axios.interceptors.response.eject(interceptor_ref.current);
		}

		interceptor_ref.current = axios.interceptors.response.use(
			function(response) {
				const header_token = (response.headers && response.headers['x-renewed-token']) ? response.headers['x-renewed-token'] : null;
				const body_token = (response.data && response.data._renewed_token) ? response.data._renewed_token : null;
				const new_token = header_token ? header_token : body_token;
				if(new_token) {
					localStorage.setItem(Constants.TOKEN_NAME, new_token);
					setToken(new_token);
				}
				return response;
			},
			function(error) {
				if(error.response && error.response.status === 401) {
					const stored_token = localStorage.getItem(Constants.TOKEN_NAME);
					if(stored_token) {
						logout(true);
					}
				}

				return Promise.reject(error);
			}
		);

		return function() {
			if(interceptor_ref.current !== null) {
				axios.interceptors.response.eject(interceptor_ref.current);
			}
		};

	}, [logout]);

	useEffect(function() {
		if(!token) {
			set_subscription_features([]);
			set_user_permissions([]);
			set_is_superadmin(false);
			set_auth_loading(false);
			return;
		}

		const user_id = localStorage.getItem(Constants.USER_ID);
		if(!user_id) {
			set_auth_loading(false);
			return;
		}

		const headers = { Authorization: 'Bearer ' + token };

		Promise.allSettled([
			axios.get(Constants.SERVER_URL + '/api/admin/check', { headers }),
			axios.get(Constants.SERVER_URL + '/api/get_subscription_features', { headers }),
			axios.get(Constants.SERVER_URL + '/api/user_permissions', { headers }),
		]).then(function(results) {
			const admin_result = results[0];
			const features_result = results[1];
			const permissions_result = results[2];

			if(admin_result.status === 'fulfilled') {
				set_is_superadmin(admin_result.value.data.is_admin || false);
			} else {
				set_is_superadmin(false);
			}

			if(features_result.status === 'fulfilled') {
				set_subscription_features(features_result.value.data.features || []);
			} else {
				set_subscription_features([]);
			}

			if(permissions_result.status === 'fulfilled') {
				set_user_permissions(permissions_result.value.data.permissions || []);
			} else {
				set_user_permissions([]);
			}

			set_auth_loading(false);
		});

	}, [token]);

	// A superadmin's session is kept alive while a map build is in progress, on whatever page is open
	useEffect(function() {
		const is_signed_in_superadmin = Boolean(token) && is_superadmin;
		if(!is_signed_in_superadmin) {
			return undefined;
		}
	}, [token, is_superadmin]);

	const auth_context = useMemo(function() {
		return {
			token,
			setToken,
			logout,
			subscription_features,
			user_permissions,
			is_superadmin,
			auth_loading,
		};
	}, [token, logout, subscription_features, user_permissions, is_superadmin, auth_loading]);

	return (
		<AuthContext.Provider value={auth_context}>
			{props.children}
		</AuthContext.Provider>
	);
}

export function useAuth() {
	return useContext(AuthContext);
}
